import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { StripeTerminalConnection } from "@/components/StripeTerminalConnection";
import { useApp } from "@/contexts/AppContext";

interface StripeAccount {
  _id: string;
  stripeAccountId: string;
  currency: string;
  locationId?: string;
}

interface TerminalLocation {
  id: string;
  displayName: string;
}

export function SettingsStripe() {
  const { refreshStripeAccount, disconnectReader } = useApp();

  const [stripeAccount, setStripeAccount] = useState<StripeAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnectingStripe, setIsConnectingStripe] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [locationId, setLocationId] = useState("");
  const [isSavingTerminal, setIsSavingTerminal] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [terminalLocations, setTerminalLocations] = useState<TerminalLocation[]>([]);

  useEffect(() => {
    async function fetchStripeAccount() {
      setIsLoading(true);
      try {
        const stripeResponse = await fetch("/api/stripe-account");
        if (stripeResponse.ok) {
          const stripeData = await stripeResponse.json();
          if (stripeData.stripeAccount) {
            setStripeAccount(stripeData.stripeAccount);
            setLocationId(stripeData.stripeAccount.locationId || "");
          }
        } else if (stripeResponse.status !== 404) {
          console.error("Failed to fetch Stripe account");
        }
      } catch (error) {
        console.error("Failed to fetch Stripe account:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchStripeAccount();
  }, []);

  const handleConnectStripe = async () => {
    setIsConnectingStripe(true);
    try {
      const response = await fetch("/api/stripe-account/connect", {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          throw new Error("No connect URL returned");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to initiate Stripe Connect");
      }
    } catch (error) {
      console.error("Error connecting Stripe:", error);
      toast.error(error instanceof Error ? error.message : "Failed to connect Stripe");
      setIsConnectingStripe(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!confirm("Are you sure you want to disconnect your Stripe account?")) {
      return;
    }

    setIsDisconnecting(true);
    try {
      const response = await fetch("/api/stripe-account/disconnect", {
        method: "POST",
      });

      if (response.ok) {
        setStripeAccount(null);
        setLocationId("");
        setTerminalLocations([]);
        await disconnectReader();
        await refreshStripeAccount();
        toast.success("Stripe account disconnected successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to disconnect Stripe account");
      }
    } catch (error) {
      console.error("Error disconnecting Stripe:", error);
      toast.error(error instanceof Error ? error.message : "Failed to disconnect Stripe account");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleLoadLocations = async () => {
    setIsLoadingLocations(true);
    try {
      const response = await fetch("/api/stripe-terminal/locations");
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to load Terminal locations");
      }

      const data = await response.json();
      setTerminalLocations(data.locations || []);

      if (!data.locations?.length) {
        toast.error("No Terminal locations found in your Stripe account");
      }
    } catch (error) {
      console.error("Error loading Terminal locations:", error);
      toast.error(error instanceof Error ? error.message : "Failed to load locations");
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const handleSaveTerminal = async () => {
    setIsSavingTerminal(true);
    try {
      const response = await fetch("/api/stripe-account/terminal", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locationId: locationId.trim() || null }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save Terminal settings");
      }

      const data = await response.json();
      setStripeAccount(data.stripeAccount);
      setLocationId(data.stripeAccount.locationId || "");
      await refreshStripeAccount();
      toast.success("Terminal settings saved");
    } catch (error) {
      console.error("Error saving Terminal settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save Terminal settings");
    } finally {
      setIsSavingTerminal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">Stripe</h3>
            <p className="text-sm text-muted-foreground">
              Connect your Stripe account to accept payments
            </p>
            {stripeAccount && (
              <p className="mt-2 text-sm text-green-600">
                Connected · Account ID: {stripeAccount.stripeAccountId}
              </p>
            )}
          </div>
          {!stripeAccount ? (
            <Button onClick={handleConnectStripe} disabled={isConnectingStripe}>
              {isConnectingStripe ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                "Connect"
              )}
            </Button>
          ) : (
            <Button
              onClick={handleDisconnectStripe}
              disabled={isDisconnecting}
              variant="destructive"
            >
              {isDisconnecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                "Disconnect"
              )}
            </Button>
          )}
        </div>
      </div>

      {stripeAccount && (
        <div className="space-y-4 rounded-lg border p-4">
          <div>
            <h3 className="text-lg font-semibold">Stripe Terminal</h3>
            <p className="text-sm text-muted-foreground">
              Set up in-person card payments with Stripe Terminal readers.
            </p>
          </div>

          <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            <li>
              In the{" "}
              <a
                href={`https://dashboard.stripe.com/${stripeAccount.stripeAccountId}/terminal/locations`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Stripe Dashboard
              </a>
              , create a Terminal location for your venue.
            </li>
            <li>Register your reader to that location (WisePOS E, S700, or Tap to Pay).</li>
            <li>Select the location below, or paste its location ID.</li>
          </ol>

          <div className="space-y-2">
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="terminal-location">Terminal location</Label>
                {terminalLocations.length > 0 ? (
                  <select
                    id="terminal-location"
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                  >
                    <option value="">Select a location</option>
                    {terminalLocations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.displayName} ({location.id})
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input
                    id="terminal-location"
                    placeholder="tml_..."
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                  />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleLoadLocations}
                disabled={isLoadingLocations}
              >
                {isLoadingLocations ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Load locations"
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Leave empty to use simulated readers while testing. Production in-person payments
              require a registered location and reader.
            </p>
          </div>

          <Button onClick={handleSaveTerminal} disabled={isSavingTerminal}>
            {isSavingTerminal ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Terminal settings"
            )}
          </Button>

          <div className="border-t pt-4">
            <StripeTerminalConnection />
          </div>
        </div>
      )}
    </div>
  );
}
